import React, { useState, useRef } from 'react';
import { Button, /* Form, */ Dropdown, Alert } from 'react-bootstrap';

const DocumentsSection = ({
  fileInputRef,
  uploadedFiles,
  documentIds = [],
  documentUploadProgress = {},
  uploadError,
  setUploadError,
  isDragging,
  documentPages,
  documentPageSvgs = {},
  getDocumentThumbnailSvg,
  handleFileInputChange,
  handleDocumentAction,
  handleUploadClick,
  handleDragEnter,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleDropboxUpload,
  handleGoogleDriveUpload,
  handleOneDriveUpload,
  handleBoxUpload,
  uploadOptions,
  replaceFileIndex,
  setReplaceFileIndex,
  showCloudUpload = false,
  onReorderDocuments,
  idPrefix = '',
  deletingDocumentRowKeys = [],
  isDocumentRowUploadInFlight = () => false,
}) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragCounter = useRef(0);

  const onItemDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
    e.currentTarget.style.opacity = '0.5';
  };

  const onItemDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDragIndex(null);
    setDragOverIndex(null);
    dragCounter.current = 0;
  };

  const onItemDragEnterHandler = (e, index) => {
    e.preventDefault();
    dragCounter.current++;
    if (dragIndex !== null && index !== dragIndex) {
      setDragOverIndex(index);
    }
  };

  const onItemDragLeaveHandler = () => {
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      setDragOverIndex(null);
      dragCounter.current = 0;
    }
  };

  const onItemDragOverHandler = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onItemDropHandler = (e, toIndex) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    if (dragIndex !== null && dragIndex !== toIndex && onReorderDocuments) {
      onReorderDocuments(dragIndex, toIndex);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const getUploadProgressKey = (file, index) => {
    const docId = file?.documentId ?? file?.document_id ?? null;
    if (file?.fromApi && docId != null) return `api:${docId}`;
    if (file?.__localUploadKey) return file.__localUploadKey;
    const name = file?.name || `doc-${index}`;
    const size = file?.size || 0;
    const lm = file?.lastModified || 0;
    return `local-fallback:${name}:${size}:${lm}:${index}`;
  };

  const isDeletingThisDocument = (file, index) => {
    const key = getUploadProgressKey(file, index);
    return Array.isArray(deletingDocumentRowKeys) && deletingDocumentRowKeys.includes(key);
  };

  const renderDocumentItem = (file, index) => {
    const progress = documentUploadProgress[getUploadProgressKey(file, index)] || null;
    const progressStatus = progress?.status || null;
    const rawProgressPercent = Number.isFinite(Number(progress?.percent)) ? Number(progress.percent) : 0;
    const shouldHoldAtProcessing =
      (progressStatus === 'uploading' || progressStatus === 'processing') && rawProgressPercent >= 100;
    const effectiveProgressStatus = shouldHoldAtProcessing ? 'processing' : progressStatus;
    const progressPercent = shouldHoldAtProcessing ? 95 : rawProgressPercent;
    const isProgressActive = ['uploading', 'processing', 'downloading', 'extracting', 'preparing_svg', 'pending'].includes(effectiveProgressStatus);
    const progressLabelByStatus = {
      uploading: 'Uploading',
      processing: 'Processing on server',
      downloading: 'Downloading ZIP',
      extracting: 'Extracting PDFs',
      preparing_svg: 'Generating SVG',
      pending: 'Queued',
      ready: 'Ready',
      error: 'Failed',
      complete: 'Complete',
    };
    const progressLabel = progressLabelByStatus[effectiveProgressStatus] || 'Processing';
    const docId = documentIds[index] ?? file?.documentId ?? file?.document_id ?? null;
    const hasServerDocumentId = docId !== undefined && docId !== null && docId !== '' && docId !== 0;
    const showWaiting = !isProgressActive && !hasServerDocumentId && !file?.fromApi;
    const isThisDocumentUploading = isDocumentRowUploadInFlight(index);
    const replaceRenameBlocked =
      isDeletingThisDocument(file, index) || isThisDocumentUploading;
    const resolvedPageCount = Number(documentPages?.[index]);
    const hasPageCount = Number.isFinite(resolvedPageCount) && resolvedPageCount > 0;
    const pageLabel = hasPageCount
      ? `${resolvedPageCount} page${resolvedPageCount !== 1 ? 's' : ''}`
      : 'Calculating pages...';
    const firstPageSvg = documentPageSvgs?.[index]?.[1] || getDocumentThumbnailSvg?.(file, index) || null;

    return (
      <div
      key={index}
      className={`document-preview-item${dragOverIndex === index ? ' drag-over' : ''}${dragIndex === index ? ' dragging' : ''}`}
      draggable
      onDragStart={(e) => onItemDragStart(e, index)}
      onDragEnd={onItemDragEnd}
      onDragEnter={(e) => onItemDragEnterHandler(e, index)}
      onDragLeave={onItemDragLeaveHandler}
      onDragOver={onItemDragOverHandler}
      onDrop={(e) => onItemDropHandler(e, index)}
    >
      <div className="document-drag-handle" title="Drag to reorder">
        <i className="ri-draggable"></i>
      </div>
      <div className="document-thumbnail">
        {firstPageSvg ? (
          <div
            className="document-thumbnail-svg-host"
            style={{
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
            }}
            dangerouslySetInnerHTML={{ __html: firstPageSvg }}
          />
        ) : (
          <div
            className="d-flex align-items-center justify-content-center text-muted"
            style={{ width: '100%', height: '100%', fontSize: '10px', textAlign: 'center', padding: '4px' }}
          >
            Generating SVG...
          </div>
        )}
      </div>
      <div className="document-info">
        <div className="document-name">{file.name}</div>
        <div className="document-pages">
          {pageLabel}
        </div>
        {isProgressActive && (
          <div className="mt-2" style={{ minWidth: 0 }}>
            <div
              style={{
                height: '6px',
                width: '100%',
                backgroundColor: '#e9ecef',
                borderRadius: '999px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.max(0, Math.min(100, progressPercent))}%`,
                  backgroundColor: '#0d6efd',
                  transition: 'width 120ms linear',
                }}
              />
            </div>
            <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
              {progressLabel}... {Math.max(0, Math.min(100, progressPercent))}%
            </div>
          </div>
        )}
        {effectiveProgressStatus === 'ready' && (
          <div className="mt-2 text-success" style={{ fontSize: '11px' }}>
            SVG ready
          </div>
        )}
        {showWaiting && (
          <div className="mt-2" style={{ minWidth: 0 }}>
            <div
              style={{
                height: '6px',
                width: '100%',
                backgroundColor: '#e9ecef',
                borderRadius: '999px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: '100%',
                  backgroundColor: '#adb5bd',
                }}
              />
            </div>
            <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
              Waiting to upload...
            </div>
          </div>
        )}
      </div>
      <div
        className="document-menu-wrapper flex-shrink-0"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onDragStart={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        style={{ zIndex: 2 }}
      >
        <Dropdown>
          <Dropdown.Toggle
            as="button"
            type="button"
            className="text-reset d-inline-block arrow-none document-menu-btn"
            id={`${idPrefix}document-menu-${index}`}
            disabled={isDeletingThisDocument(file, index)}
            style={
              isDeletingThisDocument(file, index)
                ? { pointerEvents: 'none', opacity: 0.85, cursor: 'progress' }
                : { cursor: 'pointer' }
            }
          >
            {isDeletingThisDocument(file, index) ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-label="Deleting document"></span>
            ) : (
              <i className="ri-more-2-fill"></i>
            )}
          </Dropdown.Toggle>
          <Dropdown.Menu align="end">
            <Dropdown.Item
              onClick={() => {
                if (replaceRenameBlocked) return;
                if (idPrefix) {
                  setReplaceFileIndex(index);
                  fileInputRef.current?.click();
                } else {
                  handleDocumentAction('replace', index);
                }
              }}
              disabled={replaceRenameBlocked}
            >
              <i className="ri-refresh-line me-2"></i>
              Replace
            </Dropdown.Item>
            <Dropdown.Item onClick={() => handleDocumentAction('download', index)} disabled={isDeletingThisDocument(file, index)}>
              <i className="ri-download-line me-2"></i>
              Download Document
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => handleDocumentAction('rename', index)}
              disabled={replaceRenameBlocked}
            >
              <i className="ri-edit-line me-2"></i>
              Rename Document
            </Dropdown.Item>
            <Dropdown.Item onClick={() => handleDocumentAction('delete', index)} disabled={isDeletingThisDocument(file, index)}>
              {isDeletingThisDocument(file, index) ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Deleting...
                </>
              ) : (
                <>
                  <i className="ri-delete-bin-line me-2"></i>
                  Delete Document
                </>
              )}
            </Dropdown.Item>
            <Dropdown.Item onClick={() => handleDocumentAction('view', index)} disabled={isDeletingThisDocument(file, index)}>
              <i className="ri-eye-line me-2"></i>
              View Document
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      </div>
    );
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        multiple
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      
      {uploadError && (
        <Alert variant="danger" className="mb-3" dismissible onClose={() => setUploadError('')}>
          {uploadError}
        </Alert>
      )}

      {!idPrefix ? (
        <div className={`documents-layout ${uploadedFiles.length > 0 ? 'has-files' : ''}`}>
          {uploadedFiles.length > 0 && (
            <div className="documents-preview">
              {uploadedFiles.map((file, index) => renderDocumentItem(file, index))}
            </div>
          )}

          <div
            className={`dropzone ${isDragging ? 'dragging' : ''}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={(e) => {
              if (!e.target.closest('.upload-button-wrapper')) {
                handleUploadClick();
              }
            }}
          >
            <div className="dropzone-content">
              <div className="upload-icon">
                <i className="ri-arrow-up-line"></i>
              </div>
              <p className="dropzone-text">Drop your files here or</p>
              <div className="upload-button-wrapper" onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="primary"
                  onClick={handleUploadClick}
                >
                  Upload
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {uploadedFiles.length > 0 && (
            <div className="documents-preview mb-3">
              {uploadedFiles.map((file, index) => renderDocumentItem(file, index))}
            </div>
          )}

          <div
            className={`dropzone ${isDragging ? 'dragging' : ''}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={(e) => {
              if (!e.target.closest('.upload-button-wrapper')) {
                handleUploadClick();
              }
            }}
          >
            <div className="dropzone-content">
              <div className="upload-icon">
                <i className="ri-arrow-up-line"></i>
              </div>
              <p className="dropzone-text">Drop your files here or</p>
              <div className="upload-button-wrapper" onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="primary"
                  onClick={handleUploadClick}
                >
                  Browse
                </Button>
              </div>
              <p className="dropzone-hint">Supports: PDF files</p>
            </div>
          </div>
        </>
      )}

      {showCloudUpload && (
        <div className="mt-3">
          <Dropdown>
            <Dropdown.Toggle
              variant="primary"
              className="btn-upload-dropdown"
              id={`${idPrefix}upload-dropdown`}
            >
              <i className="ri-cloud-upload-line me-2"></i>
              Upload from Cloud
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {uploadOptions.map((option, index) => (
                <Dropdown.Item
                  key={index}
                  onClick={() => {
                    if (option.label === 'Dropbox') {
                      handleDropboxUpload();
                    } else if (option.label === 'Google Drive') {
                      handleGoogleDriveUpload();
                    } else if (option.label === 'OneDrive') {
                      handleOneDriveUpload();
                    } else if (option.label === 'Box') {
                      handleBoxUpload();
                    }
                  }}
                >
                  <i className={`${option.icon} me-2`}></i>
                  {option.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      )}
    </>
  );
};

export default DocumentsSection;
